/**
 * Playback State Service (Sprint 6C — UMXP).
 *
 * Authoritative read model over PlaybackSession (+ optional Timeline).
 * Systems must consume this service (or the session it wraps) — not parallel copies.
 */
import { PlayerEvent } from "./PlayerEvents";
import { PlaybackPlatformEvent } from "./PlaybackPlatformEvents";

/**
 * @param {object} options
 * @param {object} options.session PlaybackSession
 * @param {object} [options.timeline] PlaybackTimeline
 * @param {object} [options.pipeline] PlaybackPipeline
 */
export function createPlaybackStateService(options) {
  options = options || {};
  var session = options.session;
  var timeline = options.timeline || null;
  var pipeline = options.pipeline || null;
  var engine = null;
  var unsubs = [];
  var pluginsRegistered = [];

  if (!session) {
    throw new Error("PlaybackStateService requires a PlaybackSession");
  }

  function snap() {
    return session.snapshot();
  }

  function getSessionId() {
    return session.getId();
  }

  function getPosition() {
    var s = snap();
    return (s.playback && s.playback.currentTime) || 0;
  }

  function getDuration() {
    var s = snap();
    return (s.playback && s.playback.duration) || 0;
  }

  function getStatus() {
    var s = snap();
    return (s.playback && s.playback.state) || "idle";
  }

  function getProvider() {
    return snap().provider || null;
  }

  function getContent() {
    return snap().content || null;
  }

  function getIntro() {
    return snap().intro || null;
  }

  function getResume() {
    return snap().resume || null;
  }

  function getEntitlements() {
    return snap().entitlements || null;
  }

  function getAnalytics() {
    return snap().analytics || null;
  }

  function getPlugins() {
    return pluginsRegistered.slice();
  }

  function registerPluginName(name) {
    if (name && pluginsRegistered.indexOf(name) === -1) {
      pluginsRegistered.push(name);
      session.update("plugins", { registered: pluginsRegistered.slice() });
    }
  }

  function getTimeline() {
    return timeline;
  }

  function getPipelinePhase() {
    if (pipeline && typeof pipeline.getPhase === "function") {
      return pipeline.getPhase();
    }
    return snap().pipelinePhase;
  }

  /**
   * Full authoritative snapshot for Host / plugins / diagnostics.
   */
  function getState() {
    var s = snap();
    return {
      sessionId: s.sessionId,
      playback: s.playback,
      position: getPosition(),
      duration: getDuration(),
      status: getStatus(),
      provider: s.provider,
      content: s.content,
      intro: s.intro,
      resume: s.resume,
      entitlements: s.entitlements,
      analytics: s.analytics,
      watchHistory: s.watchHistory,
      plugins: pluginsRegistered.slice(),
      experience: s.experience,
      live: s.live,
      pipelinePhase: getPipelinePhase(),
      timeline: timeline ? timeline.snapshot() : null,
      viewer: s.viewer,
      organization: s.organization,
    };
  }

  function syncFromEngineState(st) {
    if (!st) return;
    session.update(
      "playback",
      {
        currentTime: st.currentTime || 0,
        duration: st.duration || 0,
        state: st.playbackState || st.state || getStatus(),
        muted: !!st.muted,
        volume: st.volume != null ? st.volume : 1,
      },
      { silent: true }
    );
    if (timeline && st.duration) {
      timeline.setContentDuration(st.duration);
    }
  }

  /**
   * Bind engine Event Bus — sole writer of playback position/status into session.
   * @param {object} nextEngine
   */
  function attachEngine(nextEngine) {
    detachEngine();
    engine = nextEngine;
    if (!engine) return;

    var bus =
      typeof engine.getEventBus === "function" ? engine.getEventBus() : engine;

    if (session.bindBus) {
      session.bindBus(bus);
    }

    function onProgress(payload) {
      if (!engine || typeof engine.getState !== "function") {
        return;
      }
      var st = engine.getState();
      syncFromEngineState(
        Object.assign({}, st, {
          currentTime:
            (payload && payload.currentTime != null
              ? payload.currentTime
              : st.currentTime) || 0,
          duration:
            (payload && payload.duration != null
              ? payload.duration
              : st.duration) || 0,
        })
      );
    }

    function onState() {
      if (!engine || typeof engine.getState !== "function") {
        return;
      }
      syncFromEngineState(engine.getState());
    }

    var subscribe = bus.subscribe || bus.on;
    if (typeof subscribe === "function") {
      unsubs.push(subscribe.call(bus, PlayerEvent.TIME_UPDATE, onProgress));
      unsubs.push(subscribe.call(bus, "timeupdate", onProgress));
      unsubs.push(subscribe.call(bus, PlaybackPlatformEvent.PROGRESS, onProgress));
      unsubs.push(subscribe.call(bus, PlayerEvent.STATE_CHANGED, onState));
      unsubs.push(subscribe.call(bus, "statechange", onState));
      unsubs.push(
        subscribe.call(bus, PlayerEvent.PLAYBACK_STARTED, function () {
          session.update("playback", { state: "playing" });
        })
      );
      unsubs.push(
        subscribe.call(bus, PlayerEvent.PLAYBACK_PAUSED, function () {
          session.update("playback", { state: "paused" });
        })
      );
      unsubs.push(
        subscribe.call(bus, PlayerEvent.PLAYBACK_ENDED, function () {
          session.update("playback", { state: "ended" });
        })
      );
      unsubs.push(
        subscribe.call(bus, PlayerEvent.PROVIDER_CHANGED, function (payload) {
          session.setProvider({
            name: (payload && payload.provider) || null,
            surfaceKind: (payload && payload.surfaceKind) || null,
            capabilities:
              typeof engine.getCapabilities === "function"
                ? engine.getCapabilities()
                : null,
          });
        })
      );
    }

    // Seed provider immediately
    session.setProvider({
      name:
        typeof engine.getProviderName === "function"
          ? engine.getProviderName()
          : null,
      surfaceKind:
        typeof engine.getSurfaceKind === "function"
          ? engine.getSurfaceKind()
          : null,
      capabilities:
        typeof engine.getCapabilities === "function"
          ? engine.getCapabilities()
          : null,
    });
  }

  function detachEngine() {
    unsubs.forEach(function (u) {
      if (typeof u === "function") u();
    });
    unsubs = [];
    engine = null;
  }

  function destroy() {
    detachEngine();
  }

  return {
    getSession: function () {
      return session;
    },
    getSessionId: getSessionId,
    getPosition: getPosition,
    getDuration: getDuration,
    getStatus: getStatus,
    getProvider: getProvider,
    getContent: getContent,
    getIntro: getIntro,
    getResume: getResume,
    getEntitlements: getEntitlements,
    getAnalytics: getAnalytics,
    getPlugins: getPlugins,
    registerPluginName: registerPluginName,
    getTimeline: getTimeline,
    getPipelinePhase: getPipelinePhase,
    getState: getState,
    attachEngine: attachEngine,
    detachEngine: detachEngine,
    destroy: destroy,
  };
}

export default createPlaybackStateService;
